import { useState, useEffect } from 'react'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { linkTrackerApi } from '../api'
import { CANALES, GRUPOS } from '../types'
import type { URLAcortada, URLAcortadaUpdateData } from '../types'

interface EditURLDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  url: URLAcortada | null
  onSuccess: () => void
}

export function EditURLDialog({ open, onOpenChange, url, onSuccess }: EditURLDialogProps) {
  const [loading, setLoading] = useState(false)
  const [urlDestino, setUrlDestino] = useState('')
  const [titulo, setTitulo] = useState('')
  const [campana, setCampana] = useState('')
  const [canal, setCanal] = useState('')
  const [grupo, setGrupo] = useState('')

  useEffect(() => {
    if (open && url) {
      setUrlDestino(url.url_destino || '')
      setTitulo(url.titulo ?? '')
      setCampana(url.campana ?? '')
      setCanal(url.canal || 'whatsapp')
      setGrupo(url.grupo || 'tonal')
    }
  }, [open, url])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!url) return

    if (!urlDestino.trim()) {
      toast.error('La URL de destino es obligatoria')
      return
    }

    setLoading(true)
    try {
      const data: URLAcortadaUpdateData = {
        url_destino: urlDestino,
        titulo: titulo || undefined,
        campana: campana || undefined,
        canal,
        grupo,
      }

      await linkTrackerApi.actualizarURL(url.codigo, data)
      toast.success('URL actualizada correctamente')
      onOpenChange(false)
      onSuccess()
    } catch {
      toast.error('Error al actualizar la URL')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Editar URL</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-url-destino">URL de destino</Label>
            <Input
              id="edit-url-destino"
              type="url"
              value={urlDestino}
              onChange={(e) => setUrlDestino(e.target.value)}
              placeholder="https://ejemplo.com"
              disabled={loading}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-titulo">Título</Label>
            <Input
              id="edit-titulo"
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              placeholder="Título descriptivo"
              disabled={loading}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-campana">Campaña</Label>
            <Input
              id="edit-campana"
              value={campana}
              onChange={(e) => setCampana(e.target.value)}
              placeholder="Nombre de campaña"
              disabled={loading}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-canal">Canal</Label>
            <Select value={canal} onValueChange={setCanal} disabled={loading}>
              <SelectTrigger id="edit-canal">
                <SelectValue placeholder="Seleccionar canal" />
              </SelectTrigger>
              <SelectContent>
                {CANALES.map((c) => (
                  <SelectItem key={c.value} value={c.value}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-grupo">Grupo</Label>
            <Select value={grupo} onValueChange={setGrupo} disabled={loading}>
              <SelectTrigger id="edit-grupo">
                <SelectValue placeholder="Seleccionar grupo" />
              </SelectTrigger>
              <SelectContent>
                {GRUPOS.map((g) => (
                  <SelectItem key={g.value} value={g.value}>
                    {g.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
              className="border-gray-700 text-gray-700 hover:bg-gray-50 dark:border-gray-500 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-teal-800 hover:bg-teal-900 text-white"
            >
              {loading ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
